import { Field, NumericInput, RadioGroup, TextInput, TextOutput } from 'Components/Core';
import { withInjectedProps } from 'Components/Core/InjectedPropsContext';
import { ListFieldSection } from 'Components/Layout';
import { WorksheetAssociatedTestSection, WorksheetEquipmentDropdown } from 'Components/Worksheets/Core';
import { IWorksheetComponent } from 'Components/Worksheets/Core/types';
import { MaterialDryMassObtainedBy } from 'Components/Worksheets/WashWorksheet/types';
import { Guid } from 'Lib/Guid';
import { useSelector } from 'react-redux';
import { pipe } from 'rxjs';
import { WorksheetBalanceFieldSet } from '../../../../Core/WorksheetEquipment';
import externalTestMethods from '../../../../ExternalTestMethods';

const DryMassSection = withInjectedProps(({ createWorksheetSelector }: IWorksheetComponent) => {
  const hasMc =
    (useSelector(createWorksheetSelector!('Masses.InitialMoistureContentDetermined')) as boolean | undefined) ?? false;
  const importedMcId = useSelector(
    createWorksheetSelector!('Masses.AssociatedInitialMoistureContent.ImportedTest.Id')
  ) as Guid | undefined;
  const importedDryMass: boolean = useSelector(
    pipe(createWorksheetSelector!('Masses.MaterialDryMassObtainedBy'), (m) => m === MaterialDryMassObtainedBy.Imported)
  );
  return (
    <>
      <ListFieldSection heading="Initial Material Dry Mass">
        <Field
          confirm={importedDryMass || hasMc}
          confirmTitle="Water Content Deletion"
          confirmText="Are you sure you want to delete existing water content data?"
          confirmButtonText="Delete"
          input={<RadioGroup />}
          name="Masses.MaterialDryMassObtainedBy"
          label="Material Dry Mass Obtained by"
          options={[
            { label: 'Measurement as part of this test', value: MaterialDryMassObtainedBy.Measured },
            { label: 'Imported from another test', value: MaterialDryMassObtainedBy.Imported },
          ]}
        />
      </ListFieldSection>
      <ListFieldSection>
        <Field
          confirm={hasMc}
          confirmTitle="Water Content Deletion"
          confirmText="Are you sure you want to delete existing water content data?"
          confirmButtonText="Delete"
          input={<RadioGroup />}
          label="Dry Masses Determined By"
          name="Masses.InitialMoistureContentDetermined"
          options={[
            { label: 'Oven drying', value: false },
            { label: 'Wet mass and water content', value: true },
          ]}
        />
      </ListFieldSection>

      <ListFieldSection>
        <WorksheetBalanceFieldSet label="Balance" name="Equipment.InitialBalance" />
        <Field input={<WorksheetEquipmentDropdown />} label="Oven" equipmentType="oven" name="Equipment.InitialOven" />
        <Field input={<TextInput />} name="Masses.InitialTareID" label="Tare ID" maxLength={20} />
        <Field input={<NumericInput />} label="Tare Mass" name="Masses.InitialTareMass" />

        {hasMc && (
          <Field
            input={<NumericInput />}
            label="Tare and Initial Material Wet Mass"
            name="Masses.InitialTareAndWetMass"
          />
        )}
        {hasMc && <Field input={<TextOutput />} label="Initial Material Wet Mass" name="Masses.InitialWetMass" />}
        {!hasMc && <Field input={<NumericInput />} label="Tare and Dry Mass" name="Masses.InitialTareAndDryMass" />}
        {!hasMc && <Field input={<TextOutput />} label="Dry Mass" name="Masses.InitialDryMass" />}
      </ListFieldSection>

      {hasMc && (
        <WorksheetAssociatedTestSection
          associatedTestModel={{
            measured: true,
            imported: {
              importedTestResults: (
                <ListFieldSection>
                  <Field input={<TextOutput />} label="Water Content (%)" name="Masses.InitialMoistureContent" emph />
                </ListFieldSection>
              ),
              importModel: {
                Id: importedMcId ?? '',
              },
            },
            external: {
              testMethods: [externalTestMethods.moistureContent.ASTM_D2216],
              permitOtherTestMethods: true,
              externalTestResults: (
                <ListFieldSection>
                  <Field
                    input={<NumericInput />}
                    label="Water Content (%)"
                    name="Masses.AssociatedInitialMoistureContent.ExternalTest.Results.MoistureContentPercent"
                  />
                </ListFieldSection>
              ),
            },
          }}
          heading="Auxiliary Water Content"
          label="Auxiliary Water Content Determined By:"
          name="Masses.AssociatedInitialMoistureContent"
          testType="Moisture Content"
        />
      )}

      {hasMc && (
        <ListFieldSection>
          <Field input={<TextOutput />} label="Initial Material Dry Mass" name="Masses.InitialDryMass" />
        </ListFieldSection>
      )}
    </>
  );
});

export { DryMassSection };
